import { useMemo, useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES, OPTION_STATUS } from "@miplatform/constants";
import { createUUID } from "@miplatform/utils";

export const useSpecOptionsFormViewModel = ({ stateManager, planDetails }) => {
  const [optionDetailsMap, setOptionDetailsMap] = useState({});
  const [optionPriceSummaryMap, setOptionPriceSummaryMap] = useState({});
  const [optionData, setOptionData] = useState({});
  const [selectedOption, setSelectedOption] = useState(null);

  const GET_OPTION_UUID = createUUID();
  const GET_OPTION_PRICE_SUMMARY_UUID = createUUID();
  const GET_ALL_OPTION_PRICE_SUMMARIES_UUID = createUUID();
  const HANDLE_CREATE_OPTION_UUID = createUUID();
  const HANDLE_UPDATE_OPTION_UUID = createUUID();
  const HANDLE_DELETE_OPTION_UUID = createUUID();

  const previewOptionData = useMemo(() => {
    return Object.values(optionDetailsMap).reduce((acc, curr) => {
      if (curr.showPreview) {
        return [...acc, { ...curr }];
      }
      return [...acc];
    }, []);
  }, [optionDetailsMap]);

  const getOption = (selectedOption) => {
    stateManager.abort(GET_OPTION_UUID);
    return stateManager.run(async () => {
      const response = await homeService.plans.options.getById({
        planId: planDetails.id,
        optionId: selectedOption.id,
        toastMessageConfig: {
          failure: {
            text: `Failed to fetch Option.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setOptionData(response.data);
      }
    }, GET_OPTION_UUID);
  };

  const getOptionPriceSummary = async (planId, optionId) => {
    stateManager.abort(GET_OPTION_PRICE_SUMMARY_UUID);
    stateManager.run(async () => {
      const response = await homeService.pricing.options.getById({
        planId,
        optionId,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setOptionPriceSummaryMap((prev) => ({
          ...prev,
          [optionId]: response.data,
        }));
      }
    }, GET_OPTION_PRICE_SUMMARY_UUID);
  };

  const getAllOptionPriceSummaries = async (planId) => {
    stateManager.abort(GET_ALL_OPTION_PRICE_SUMMARIES_UUID);
    stateManager.run(async () => {
      const response = await homeService.pricing.options.getAll({ planId });
      if (response.status === HTTP_STATUSES.OK) {
        const newOptionPriceSummaryMap = response.data.reduce((acc, option) => {
          acc[option.optionId] = option;
          return acc;
        }, {});
        setOptionPriceSummaryMap(newOptionPriceSummaryMap);
      }
    }, GET_ALL_OPTION_PRICE_SUMMARIES_UUID);
  };

  const handleCreateOption = (data) => {
    stateManager.abort(HANDLE_CREATE_OPTION_UUID);
    return stateManager.run(async () => {
      const response = await homeService.plans.options.create({
        data,
        planId: planDetails.id,
        toastMessageConfig: {
          success: {
            text: `Successfully created Option ${data.name}.`,
          },
          failure: {
            text: `Failed to create Option.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.CREATED) {
        setOptionDetailsMap((prev) => ({
          ...prev,
          [response.data.id]: {
            ...response.data,
            showPreview: false,
            ...mapModifiedPlanAreasAndMaterials(response.data.planAreas ?? []),
          },
        }));
        setSelectedOption(response.data);
        setOptionData(response.data);
      }
      return response;
    }, HANDLE_CREATE_OPTION_UUID);
  };

  const handleUpdateOption = (optionName) => {
    stateManager.abort(HANDLE_UPDATE_OPTION_UUID);
    stateManager.run(async () => {
      // filter out any modified existing plan areas that have no materials
      const filteredPlanAreas = optionData.planAreas.filter(
        (area) =>
          !(
            area.materials.length === 0 &&
            area.optionStatus === OPTION_STATUS.EXISTING
          )
      );

      const response = await homeService.plans.options.update({
        data: {
          ...optionData,
          name: optionName,
          planAreas: filteredPlanAreas,
        },
        planId: planDetails.id,
        toastMessageConfig: {
          success: {
            text: `Successfully saved Option ${optionName}.`,
          },
          failure: {
            text: `Failed to save Option ${optionName}.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setOptionDetailsMap((prev) => ({
          ...prev,
          [response.data.id]: {
            ...response.data,
            showPreview: false,
            ...mapModifiedPlanAreasAndMaterials(response.data.planAreas ?? []),
          },
        }));
        setSelectedOption(null);
        setOptionData({});
      }
    }, HANDLE_UPDATE_OPTION_UUID);
  };

  const handleDeleteOption = (option) => {
    stateManager.abort(HANDLE_DELETE_OPTION_UUID);
    stateManager.run(async () => {
      const response = await homeService.plans.options.delete({
        id: option.id,
        modifiedOn: option.modifiedOn,
        planId: planDetails.id,
        toastMessageConfig: {
          success: {
            text: `Successfully deleted Option ${option.name}.`,
          },
          failure: {
            text: `Failed to delete Option ${option.name}.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setOptionDetailsMap((prev) => {
          const updatedMap = { ...prev };
          delete updatedMap[option.id];
          return updatedMap;
        });
        setSelectedOption(null);
        setOptionData({});
      }
    }, HANDLE_DELETE_OPTION_UUID);
  };

  // AREA -> OPTION HANDLERS

  const handleAddAreaToOption = (data) => {
    const areaData = data.map((area) => {
      return {
        ...area,
        id: createUUID(),
        label: area.areaTypeName,
        materials: [],
        optionStatus: OPTION_STATUS.ADDED,
      };
    });
    const updatedPlanAreas = [...optionData.planAreas, ...areaData];

    setOptionData((prev) => ({
      ...prev,
      planAreas: updatedPlanAreas,
    }));
  };

  const handleRemoveAreaFromOption = (area) => {
    // check if planArea already exists in the option
    const existingOptionArea = optionData.planAreas.find(
      (a) => a.referenceAreaId === area.id
    );

    const currentlyRemovedMaterials =
      existingOptionArea?.materials.filter(
        (m) => m.optionStatus === OPTION_STATUS.REMOVED
      ) ?? [];

    const updatedPlanAreas =
      area.optionStatus === OPTION_STATUS.ADDED
        ? optionData.planAreas.filter((a) => a.id !== area.id)
        : optionData.planAreas
            // replace existing area if already in option
            .filter((a) => a.id !== existingOptionArea?.id)
            .concat([
              {
                ...area,
                referenceAreaId: area.id,
                id: existingOptionArea?.id ?? createUUID(),
                optionStatus: OPTION_STATUS.REMOVED,
                materials: [...area.materials]
                  // filter any added materials and set any existing materials to IsRemoved
                  .filter((m) => m.optionStatus !== OPTION_STATUS.ADDED)
                  .map((m) => ({
                    ...m,
                    referencePlanMaterialId: m.id,
                    id: createUUID(),
                    optionStatus: OPTION_STATUS.REMOVED,
                  }))
                  .concat(currentlyRemovedMaterials),
              },
            ]);

    setOptionData((prev) => ({
      ...prev,
      planAreas: updatedPlanAreas,
    }));
  };

  const handleUndoRemoveAreaFromOption = (area) => {
    const updatedPlanAreas = optionData.planAreas.filter(
      (a) => a.referenceAreaId !== area.id
    );

    setOptionData((prev) => ({
      ...prev,
      planAreas: updatedPlanAreas,
    }));
  };

  // ITEM -> OPTION HANDLERS

  const handleAddItemToArea = ([checkedArea], material) => {
    const area = { ...checkedArea };

    // check if planArea/material already exists in the option
    const existingOptionArea = optionData.planAreas.find(
      (a) => a.id === area.id || a.referenceAreaId === area.id
    );
    const existingMaterial = existingOptionArea?.materials?.find(
      (m) =>
        m.materialId === material.id && m.optionStatus !== OPTION_STATUS.REMOVED
    );

    if (area.optionStatus === OPTION_STATUS.EXISTING) {
      // if the area exists in plan, we must add a reference to its original planArea id
      area.referenceAreaId = area.id;
      area.id = existingOptionArea?.id ?? createUUID();
    }

    const materialData = {
      ...material,
      id: createUUID(),
      quantity: 1,
      suppliedByBuilder: false,
      optionStatus: OPTION_STATUS.ADDED,
    };

    const updatedPlanAreas = existingOptionArea
      ? optionData.planAreas.map((a) => {
          if (a.id === area.id || a.referenceAreaId === area.id) {
            // if material already exists, update the quantity
            if (existingMaterial) {
              return {
                ...a,
                materials: a.materials.map((m) =>
                  m.materialId === material.id &&
                  m.optionStatus !== OPTION_STATUS.REMOVED
                    ? {
                        ...m,
                        quantity: m.quantity + 1,
                        optionStatus: OPTION_STATUS.ADDED,
                      }
                    : m
                ),
              };
            }
            // if not, add the new material to the existing area
            return {
              ...a,
              materials: [...a.materials, materialData],
            };
          }
          return a;
        })
      : // if the area does not exist in the option, add it
        [...optionData.planAreas, { ...area, materials: [materialData] }];

    setOptionData((prev) => ({
      ...prev,
      planAreas: updatedPlanAreas,
    }));
  };

  const handleAddItemTemplateToArea = ([checkedArea], materials = []) => {
    const area = { ...checkedArea };

    // check if planArea/material already exists in the option
    const existingOptionArea = optionData.planAreas.find(
      (a) => a.id === area.id || a.referenceAreaId === area.id
    );

    if (area.optionStatus === OPTION_STATUS.EXISTING) {
      // if the area exists in plan, we must add a reference to its original planArea id
      area.referenceAreaId = area.id;
      area.id = existingOptionArea?.id ?? createUUID();
    }

    const materialData = materials.map((material) => ({
      ...material,
      id: createUUID(),
      quantity: material.quantity || 1,
      suppliedByBuilder: material.suppliedByBuilder,
      optionStatus: OPTION_STATUS.ADDED,
    }));

    const updatedPlanAreas = existingOptionArea
      ? optionData.planAreas.map((a) => {
          if (a.id === area.id || a.referenceAreaId === area.id) {
            const updatedMaterials = a.materials.map((existingMaterial) => {
              const matchingMaterial = materialData.find(
                (material) =>
                  material.materialId === existingMaterial.materialId &&
                  existingMaterial.optionStatus !== OPTION_STATUS.REMOVED
              );

              // replace with item template quantity & suppliedBuilder if matching material is found
              return matchingMaterial
                ? {
                    ...existingMaterial,
                    quantity: matchingMaterial.quantity,
                    suppliedByBuilder: matchingMaterial.suppliedByBuilder,
                  }
                : existingMaterial;
            });

            // add new materials that are not already in the area
            const newMaterialData = materialData.filter(
              (material) =>
                !a.materials.some(
                  (existingMaterial) =>
                    existingMaterial.materialId === material.materialId &&
                    existingMaterial.optionStatus !== OPTION_STATUS.REMOVED
                )
            );

            // if not, add the new material to the existing area
            return {
              ...a,
              materials: [...updatedMaterials, ...newMaterialData],
            };
          }
          return a;
        })
      : // if the area does not exist in the option, add it
        [...optionData.planAreas, { ...area, materials: materialData }];

    setOptionData((prev) => ({
      ...prev,
      planAreas: updatedPlanAreas,
    }));
  };

  const handleRemoveItemFromArea = (area, material) => {
    // check if planArea/material already exists in the option
    const existingOptionArea = optionData.planAreas.find(
      (a) => a.id === area.id || a.referenceAreaId === area.id
    );
    const existingMaterial = existingOptionArea?.materials?.find(
      (m) => m.id === material.id && m.optionStatus === material.optionStatus
    );

    const areaData = { ...area };
    if (areaData.optionStatus === OPTION_STATUS.EXISTING) {
      // if the area exists in plan, we must add a reference to its original planArea id
      areaData.referenceAreaId = areaData.id;
      areaData.id = existingOptionArea?.id ?? createUUID();
    }

    const materialData = {
      ...material,
      optionStatus: OPTION_STATUS.REMOVED,
    };
    if (material.optionStatus === OPTION_STATUS.EXISTING) {
      // if the material exists in plan, we must add a reference to its original planMaterial id
      materialData.referencePlanMaterialId = material.id;
      materialData.id = existingMaterial?.id ?? createUUID();
    }

    const updatedPlanAreas = existingOptionArea
      ? // if the area is currently in the option, we need to update it (IsAdded or IsExisting)
        optionData.planAreas.map((a) => {
          if (a.id === area.id || a.referenceAreaId === area.id) {
            if (
              existingMaterial &&
              existingMaterial.optionStatus === OPTION_STATUS.ADDED
            ) {
              // if material exists and is added, remove it from the area
              return {
                ...a,
                materials: a.materials.filter(
                  (m) => m.id !== existingMaterial.id
                ),
              };
            } // if not, add the removed material to the area
            return {
              ...a,
              materials: [...a.materials, materialData],
            };
          }
          return a;
        })
      : // if the area does not exist in the option, add it with the removed material and its referenceAreaId
        [...optionData.planAreas, { ...areaData, materials: [materialData] }];

    setOptionData((prev) => ({
      ...prev,
      planAreas: updatedPlanAreas,
    }));
  };

  const handleRemoveAllItemsFromArea = (area) => {
    // check if planArea already exists in the option
    const existingOptionArea = optionData.planAreas.find(
      (a) => a.id === area.id || a.referenceAreaId === area.id
    );

    const remainingExistingMaterials = area?.materials.filter(
      (m) => m.optionStatus === OPTION_STATUS.EXISTING
    );

    // need to filter out any added materials, as well as convert any existing materials to IsRemoved
    const areaData = existingOptionArea
      ? {
          ...existingOptionArea,
          materials: [
            ...existingOptionArea.materials,
            ...remainingExistingMaterials,
          ],
        }
      : { ...area };

    if (
      !existingOptionArea &&
      areaData.optionStatus === OPTION_STATUS.EXISTING
    ) {
      // if the area exists in plan, we must add a reference to its original planArea id
      areaData.referenceAreaId = areaData.id;
      areaData.id = existingOptionArea?.id ?? createUUID();
    }

    // filter any added materials and set remaining materials to IsRemoved
    areaData.materials = areaData.materials
      .filter((m) => m.optionStatus !== OPTION_STATUS.ADDED)
      .map((m) => {
        if (!m.referencePlanMaterialId) {
          return {
            ...m,
            referencePlanMaterialId: m.id,
            id: createUUID(),
            optionStatus: OPTION_STATUS.REMOVED,
          };
        }
        return {
          ...m,
          optionStatus: OPTION_STATUS.REMOVED,
        };
      });

    const updatedPlanAreas = existingOptionArea
      ? optionData.planAreas.map((a) => {
          if (a.id === area.id || a.referenceAreaId === area.id) {
            return areaData;
          }
          return a;
        })
      : [...optionData.planAreas, areaData];

    setOptionData((prev) => ({
      ...prev,
      planAreas: updatedPlanAreas,
    }));
  };

  const handleReturnItemToPlan = (area, material) => {
    const updatedOptionArea = optionData.planAreas.find(
      (a) => a.id === area.id || a.referenceAreaId === area.id
    );

    const filteredMaterials = updatedOptionArea?.materials.filter(
      (m) => m.id !== material.id
    );

    const updatedPlanAreas =
      filteredMaterials.length > 0
        ? optionData.planAreas.map((a) => {
            if (a.id === area.id || a.referenceAreaId === area.id) {
              return {
                ...a,
                materials: filteredMaterials,
              };
            }
            return a;
          })
        : // if no materials left in the area, remove it from the option
          optionData.planAreas.filter(
            (a) => !(a.id === area.id || a.referenceAreaId === area.id)
          );
    setOptionData((prev) => ({
      ...prev,
      planAreas: updatedPlanAreas,
    }));
  };

  const handleReturnAllItemsToPlan = (area) => {
    const updatedOptionArea = optionData.planAreas.find(
      (a) => a.id === area.id || a.referenceAreaId === area.id
    );
    const filteredMaterials = updatedOptionArea.materials.filter(
      (m) => m.optionStatus !== OPTION_STATUS.REMOVED
    );

    const updatedPlanAreas =
      filteredMaterials.length > 0
        ? optionData.planAreas.map((a) => {
            if (a.id === area.id || a.referenceAreaId === area.id) {
              return {
                ...a,
                materials: filteredMaterials,
              };
            }
            return a;
          })
        : optionData.planAreas.filter(
            (a) => !(a.id === area.id || a.referenceAreaId === area.id)
          );

    setOptionData((prev) => ({
      ...prev,
      planAreas: updatedPlanAreas,
    }));
  };

  // MERGE FUNCTIONS

  const mergeOptionAndPlanAreas = (optionPlanAreas, planAreas) => {
    const optionPlanAreasByStatus = Object.groupBy(
      optionPlanAreas,
      (area) => area.optionStatus
    );

    const addedAreas = optionPlanAreasByStatus[OPTION_STATUS.ADDED] || [];
    const existingAreas = optionPlanAreasByStatus[OPTION_STATUS.EXISTING] || [];
    const removedAreas = optionPlanAreasByStatus[OPTION_STATUS.REMOVED] || [];

    return [
      ...addedAreas,
      ...planAreas.map((area) => {
        // use referenceAreaId saved on existing areas within the option for comparison
        const matchingExistingAreas = existingAreas.filter(
          (existing) => existing.referenceAreaId === area.id
        );
        const removedExistingArea = removedAreas.find(
          (existing) => existing.referenceAreaId === area.id
        );
        if (removedExistingArea) {
          return {
            ...area,
            optionStatus: OPTION_STATUS.REMOVED,
          };
        }
        if (matchingExistingAreas.length > 0) {
          return {
            ...area,
            materials: mergeOptionAndPlanMaterials(
              // combine all materials from matching existing areas before merging with plan materials
              matchingExistingAreas.reduce(
                (acc, matchingExistingArea) => [
                  ...acc,
                  ...matchingExistingArea.materials,
                ],
                []
              ),
              area.materials
            ),
          };
        }
        return area;
      }),
    ];
  };

  const mergeOptionAndPlanMaterials = (optionMaterials, planMaterials) => {
    const optionMaterialsByStatus = Object.groupBy(
      optionMaterials,
      (material) => material.optionStatus
    );

    const addedMaterials = optionMaterialsByStatus[OPTION_STATUS.ADDED] || [];
    const removedMaterials =
      optionMaterialsByStatus[OPTION_STATUS.REMOVED] || [];

    // filter out any removed materials from the existing plan materials
    const filteredPlanMaterials = planMaterials.filter(
      (material) =>
        !removedMaterials.some(
          (removedMaterial) =>
            removedMaterial.referencePlanMaterialId === material.id
        )
    );
    return [...addedMaterials, ...removedMaterials, ...filteredPlanMaterials];
  };

  const mapModifiedPlanAreasAndMaterials = (planAreas) => {
    const modifiedMaterialIds = [];
    planAreas.forEach((area) => {
      area.materials.forEach((material) => {
        if (material.referencePlanMaterialId) {
          modifiedMaterialIds.push(material.referencePlanMaterialId);
        }
      });
    });

    const modifiedExistingAreaIds = planAreas
      .filter((area) => area.optionStatus === OPTION_STATUS.EXISTING)
      .map((area) => area.referenceAreaId);

    const removedAreaIds = planAreas
      .filter((area) => area.optionStatus === OPTION_STATUS.REMOVED)
      .map((area) => area.referenceAreaId);

    return {
      modifiedMaterialIds,
      modifiedExistingAreaIds,
      removedAreaIds,
    };
  };

  const collectModifiedIds = (dataKey, option) => {
    return previewOptionData.reduce(
      (acc, curr) =>
        curr.id !== option.id ? [...acc, ...(curr[dataKey] ?? [])] : acc,
      []
    );
  };

  const hasConflicts = (uniqueSet, ids = []) => {
    return ids.some((item) => uniqueSet.has(item));
  };

  const getPreviewCompatibility = (option) => {
    const [modifiedMaterialIds, modifiedExistingAreaIds, removedAreaIds] = [
      collectModifiedIds("modifiedMaterialIds", option),
      collectModifiedIds("modifiedExistingAreaIds", option),
      collectModifiedIds("removedAreaIds", option),
    ].map((ids) => new Set(ids));

    return (
      hasConflicts(modifiedMaterialIds, option?.modifiedMaterialIds) ||
      hasConflicts(modifiedExistingAreaIds, option?.removedAreaIds) ||
      hasConflicts(removedAreaIds, option?.modifiedExistingAreaIds)
    );
  };

  return {
    optionDetailsMap,
    setOptionDetailsMap,
    getOption,
    optionData,
    setOptionData,
    selectedOption,
    setSelectedOption,
    previewOptionData,
    getPreviewCompatibility,
    optionPriceSummaryMap,
    getOptionPriceSummary,
    getAllOptionPriceSummaries,
    mapModifiedPlanAreasAndMaterials,
    handleCreateOption,
    handleUpdateOption,
    handleDeleteOption,
    handleAddAreaToOption,
    handleRemoveAreaFromOption,
    handleUndoRemoveAreaFromOption,
    handleAddItemToArea,
    handleAddItemTemplateToArea,
    handleRemoveItemFromArea,
    handleRemoveAllItemsFromArea,
    handleReturnItemToPlan,
    handleReturnAllItemsToPlan,
    mergeOptionAndPlanAreas,
  };
};
